import { Howl } from 'howler';
import { SoundFont } from './SoundFont';
import { SidSynth } from './SidSynth';
import soundfontConfig from '../assets/audio/sfx/soundfont.json';

/**
 * Unified Audio Manager using Howler.js with SoundFont support and unique Level BGMs
 */
export class AudioManager {
  constructor() {
    this.muted = false;
    this.synth = new SidSynth();
    this.soundFont = null;
    this.currentBGM = null;
    this.currentLevel = 0;
    this.bgmPlaying = false;
    this.levelTracks = new Map();

    // Initialize Howler SoundFont
    this.soundFont = new SoundFont(soundfontConfig, () => {
      console.log('Audio: SoundFont active');
    });

    // Pre-create Menu Theme
    this.menuBGM = new Howl({
      src: [
        'assets/audio/music/arkanoid_c64_theme.mp3',
        'assets/audio/music/arkanoid_c64_theme.wav',
      ],
      loop: true,
      volume: 0.45,
      html5: true,
    });
  }

  unlockAudio() {
    this.synth.ensureContext();
  }

  toggleMute() {
    this.muted = !this.muted;
    this.synth.setMuted(this.muted);
    if (this.soundFont) this.soundFont.setMute(this.muted);
    if (this.menuBGM) this.menuBGM.mute(this.muted);
    this.levelTracks.forEach((track) => track.mute(this.muted));
    return this.muted;
  }

  setMuted(muted) {
    this.muted = muted;
    this.synth.setMuted(this.muted);
    if (this.soundFont) this.soundFont.setMute(this.muted);
    if (this.menuBGM) this.menuBGM.mute(this.muted);
    this.levelTracks.forEach((track) => track.mute(this.muted));
  }

  playMenuBGM() {
    this.stopBGM();
    if (this.muted) return;
    try {
      this.menuBGM.mute(this.muted);
      this.menuBGM.play();
      this.currentBGM = this.menuBGM;
      this.bgmPlaying = true;
    } catch (e) {
      console.warn('Could not start menu BGM', e);
    }
  }

  playLevelBGM(level = 1) {
    this.stopBGM();
    this.currentLevel = level;
    if (this.muted) return;

    try {
      if (!this.levelTracks.has(level)) {
        const track = new Howl({
          src: [
            `assets/audio/music/arkanoid_level${level}.mp3`,
            `assets/audio/music/arkanoid_level${level}.wav`,
          ],
          loop: true,
          volume: 0.45,
          html5: false,
        });
        this.levelTracks.set(level, track);
      }

      const activeTrack = this.levelTracks.get(level);
      if (activeTrack) {
        activeTrack.mute(this.muted);
        activeTrack.play();
        this.currentBGM = activeTrack;
        this.bgmPlaying = true;
      }
    } catch (e) {
      console.warn(`Could not start Level ${level} BGM`, e);
    }
  }

  stopBGM() {
    if (this.currentBGM) {
      try {
        this.currentBGM.stop();
      } catch (e) {}
      this.currentBGM = null;
    }
    if (this.menuBGM) {
      try {
        this.menuBGM.stop();
      } catch (e) {}
    }
    this.bgmPlaying = false;
  }

  // Sound effects: Attempts Howler SoundFont first, then SID synth fallback
  playPaddleBounce() {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('paddle_bounce')) {
      this.synth.playPaddleBounce();
    }
  }

  playBrickHit(colorIndex) {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('brick_hit')) {
      this.synth.playBrickHit(colorIndex);
    }
  }

  playBrickDestroy() {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('brick_shattered')) {
      this.synth.playBrickShattered();
    }
  }

  playSilverHit() {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('silver_hit')) {
      this.synth.playSilverHit();
    }
  }

  playLaserFire() {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('laser_fire')) {
      this.synth.playLaserFire();
    }
  }

  playPowerupSpawn() {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('powerup_spawn')) {
      this.synth.playPowerupSpawn();
    }
  }

  playPowerupCollect() {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('powerup_collect')) {
      this.synth.playPowerupCollect();
    }
  }

  playExtraLife() {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('extra_life')) {
      this.synth.playExtraLife();
    }
  }

  playBallLost() {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('ball_lost')) {
      this.synth.playBallLost();
    }
  }

  playLevelClear() {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('level_clear')) {
      this.synth.playLevelClear();
    }
  }

  playGameOver() {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('game_over')) {
      this.synth.playGameOver();
    }
  }

  playMenuSelect() {
    if (this.muted) return;
    if (!this.soundFont || !this.soundFont.play('menu_select')) {
      this.synth.playMenuSelect();
    }
  }
}
